import { useState } from "react";
import { track } from "../services/analytics";
import { formatTranscriptionText } from "../utils";

const ICON = {
  rewind:
    "M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8L12.066 11.2zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8L4.066 11.2z",
  forward:
    "M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z",
  copy: "M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z",
  alert: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  clock: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
};

const Icon = ({ d, className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
  </svg>
);

const SKIP_BUTTON =
  "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700";

// "Oct 15, 3:27 PM"
const formatCallTime = (iso) =>
  new Date(iso).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

// 42 -> "0:42"; nothing when the length is unknown.
const formatDuration = (seconds) =>
  seconds > 0
    ? `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, "0")}`
    : null;

// A transcript this short fits the three-line preview, so it needs no expand.
const PREVIEW_CHARS = 160;

// The call recordings list, newest first: one quiet row per call with its
// transcript first and the player under it. Shared by the side pane and the
// full lead page; each view draws its own section around it.
const CallRecordings = ({ lead }) => {
  const [expandedTranscripts, setExpandedTranscripts] = useState(new Set());
  const [hasViewedTranscript, setHasViewedTranscript] = useState(() => {
    return localStorage.getItem("hasViewedTranscriptFeature") === "true";
  });

  const toggleTranscript = (recording) => {
    // Mark feature as viewed on first click
    if (!hasViewedTranscript) {
      setHasViewedTranscript(true);
      localStorage.setItem("hasViewedTranscriptFeature", "true");
    }

    setExpandedTranscripts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(recording.id)) {
        newSet.delete(recording.id);
      } else {
        newSet.add(recording.id);
        track("call_transcript_opened", {
          lead_id: lead.id,
          duration_seconds: recording.duration,
        });
      }
      return newSet;
    });
  };

  return (
    <div className="divide-y divide-border">
      {[...lead.call_recordings]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .map((recording) => {
          const expanded = expandedTranscripts.has(recording.id);
          const hasTranscript =
            recording.transcription_status === "completed" &&
            recording.transcription_text;
          const canExpand =
            hasTranscript && recording.transcription_text.length > PREVIEW_CHARS;
          const header = ["Call", formatCallTime(recording.created_at), formatDuration(recording.duration)]
            .filter(Boolean)
            .join(" · ");
          return (
            <div key={recording.id} className="space-y-2 py-3 first:pt-0 last:pb-0">
              <p className="text-xs text-gray-500">{header}</p>

              {hasTranscript && (
                <div className="relative border-l-2 border-primary/40 pl-3 pr-8">
                  <button
                    type="button"
                    data-attr="transcript-copy"
                    aria-label="Copy transcript"
                    title="Copy transcript"
                    onClick={() => navigator.clipboard.writeText(recording.transcription_text)}
                    className="absolute right-0 top-0 rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  >
                    <Icon d={ICON.copy} className="h-3.5 w-3.5" />
                  </button>
                  {expanded ? (
                    <div className="ph-mask max-h-64 space-y-2 overflow-y-auto text-sm text-gray-700">
                      {formatTranscriptionText(recording.transcription_text).map((sentence, idx) => (
                        <p key={idx} className="mb-0 leading-relaxed">
                          {sentence}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="ph-mask line-clamp-3 text-sm leading-relaxed text-gray-700">
                      {recording.transcription_text}
                    </p>
                  )}
                  {canExpand && (
                    <button
                      type="button"
                      data-attr="transcript-expand"
                      aria-expanded={expanded}
                      onClick={() => toggleTranscript(recording)}
                      className="mt-1 text-xs font-medium text-primary hover:underline"
                    >
                      {expanded ? "Show less" : "Show full transcript"}
                      {!hasViewedTranscript && (
                        <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
                          NEW
                        </span>
                      )}
                    </button>
                  )}
                </div>
              )}

              {recording.transcription_status === "processing" && (
                <div className="flex items-center text-sm text-gray-600">
                  <svg
                    className="mr-2 h-4 w-4 animate-spin text-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Transcribing audio...
                </div>
              )}

              {recording.transcription_status === "failed" && (
                <div className="flex items-center text-sm text-red-600">
                  <Icon d={ICON.alert} className="mr-1 h-4 w-4" />
                  Transcription failed
                </div>
              )}

              {recording.transcription_status === "pending" && (
                <div className="flex items-center text-sm text-gray-500">
                  <Icon d={ICON.clock} className="mr-1 h-4 w-4" />
                  Transcription pending...
                </div>
              )}

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    const audio = document.getElementById(`audio-${recording.id}`);
                    audio.currentTime = Math.max(0, audio.currentTime - 5);
                  }}
                  className={SKIP_BUTTON}
                  title="Rewind 5 seconds"
                >
                  <Icon d={ICON.rewind} />
                </button>
                <audio
                  id={`audio-${recording.id}`}
                  controls
                  className="h-10 min-w-0 flex-1"
                  style={{ minWidth: "200px" }}
                >
                  <source src={recording.recording_url} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
                <button
                  type="button"
                  onClick={() => {
                    const audio = document.getElementById(`audio-${recording.id}`);
                    audio.currentTime = Math.min(
                      audio.duration || audio.currentTime + 5,
                      audio.currentTime + 5,
                    );
                  }}
                  className={SKIP_BUTTON}
                  title="Fast forward 5 seconds"
                >
                  <Icon d={ICON.forward} />
                </button>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default CallRecordings;
